import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import RangeCalendar from '@/components/RangeCalendar';
import { CalendarIcon, Search, Package, Clock, History, List, Check } from 'lucide-react';
import { formatDateForBackend, formatDeliveryDateDisplay } from '../utils/dateUtils';
import { getAllStatuses, getStatusConfig } from '@/components/StatusBadge';

const VIEW_FILTERS = {
  FUTURE: 'future',
  PAST: 'past',
  ALL: 'all'
};

export { VIEW_FILTERS };

export const OrderFilters = ({
  viewFilter,
  setViewFilter,
  dateRange,
  setDateRange,
  statusFilter,
  setStatusFilter,
  searchTerm,
  setSearchTerm,
  sortBy,
  toggleSort,
  orderCount,
  sources = [],
  sourceFilterStatus = 'ready',
    sourceUnavailableMessage = 'Offline source catalog unavailable. Orders remain visible; other filters still work.',
  selectedSourceIds = [],
  setSelectedSourceIds,
}) => {
  const fmtRange = (r) => {
    if (!r?.from) return 'Filter by Date';
    const from = formatDeliveryDateDisplay(formatDateForBackend(r.from));
    if (!r.to || formatDateForBackend(r.to) === formatDateForBackend(r.from)) return from;
    return `${from} – ${formatDeliveryDateDisplay(formatDateForBackend(r.to))}`;
  };
  return (
    <div className="flex flex-col gap-4">
      {/* View Filter Tabs */}
      <div className="flex flex-wrap gap-2 pb-2 border-b">
        <Button
          variant={viewFilter === VIEW_FILTERS.FUTURE && !dateRange?.from ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setViewFilter(VIEW_FILTERS.FUTURE); setDateRange(null); }}
          data-testid="view-filter-future"
          className="gap-2"
        >
          <Clock className="h-4 w-4" />
          Upcoming
        </Button>
        <Button
          variant={viewFilter === VIEW_FILTERS.PAST && !dateRange?.from ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setViewFilter(VIEW_FILTERS.PAST); setDateRange(null); }}
          data-testid="view-filter-past"
          className="gap-2"
        >
          <History className="h-4 w-4" />
          Past
        </Button>
        <Button
          variant={viewFilter === VIEW_FILTERS.ALL && !dateRange?.from ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setViewFilter(VIEW_FILTERS.ALL); setDateRange(null); }}
          data-testid="view-filter-all"
          className="gap-2"
        >
          <List className="h-4 w-4" />
          All
        </Button>
        
        {/* Source + Date filters */}
        <div className="ml-auto flex items-center gap-2">
          {/* Source multi-select filter (airtight, applied server-side) */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={selectedSourceIds.length ? 'default' : 'outline'}
                size="sm"
                className="gap-2"
                data-testid="source-filter-button"
                disabled={sourceFilterStatus !== 'ready'}
              >
                <Package className="h-4 w-4" />
                {sourceFilterStatus === 'loading'
                  ? 'Loading sources'
                  : sourceFilterStatus === 'unavailable'
                    ? 'Sources unavailable'
                    : selectedSourceIds.length
                      ? `Sources (${selectedSourceIds.length})`
                      : 'Filter by Source'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2" align="end">
              <div className="max-h-64 overflow-y-auto space-y-0.5">
                {sources.length === 0 && (
                  <p className="text-xs text-stone-400 px-2 py-1.5">No sources</p>
                )}
                {sources.map(s => {
                  const checked = selectedSourceIds.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setSelectedSourceIds(
                        checked
                          ? selectedSourceIds.filter(x => x !== s.id)
                          : [...selectedSourceIds, s.id]
                      )}
                      className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded hover:bg-stone-100 text-sm"
                      data-testid={`source-filter-option-${s.id}`}
                    >
                      <span className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${checked ? 'bg-primary border-primary text-white' : 'border-stone-300'}`}>
                        {checked && <Check className="h-3 w-3" />}
                      </span>
                      <span className="truncate">{s.name}</span>
                    </button>
                  );
                })}
              </div>
              {selectedSourceIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedSourceIds([])}
                  className="mt-1 w-full text-left text-xs text-stone-500 hover:text-stone-700 px-2 py-1"
                >
                  Clear sources
                </button>
              )}
            </PopoverContent>
          </Popover>

          {/* Date-range filter (two-click range via the shared RangeCalendar) */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={dateRange?.from ? 'default' : 'outline'}
                size="sm"
                className="gap-2"
                data-testid="date-filter-button"
              >
                <CalendarIcon className="h-4 w-4" />
                {fmtRange(dateRange)}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <RangeCalendar
                value={dateRange || undefined}
                onChange={setDateRange}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
          {dateRange?.from && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDateRange(null)}
              data-testid="clear-date-filter"
              className="text-stone-500 hover:text-stone-700"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {sourceFilterStatus === 'unavailable' && (
        <p role="status" className="text-sm text-amber-800" data-testid="source-filter-unavailable">
            {sourceUnavailableMessage}
        </p>
      )}

      {/* Status Filter */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-stone-600 mr-1">Status:</span>
        <Button
          variant={statusFilter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('all')}
          data-testid="status-filter-all"
          className="h-7 text-xs"
        >
          All
        </Button>
        {getAllStatuses().map(status => {
          const config = getStatusConfig(status);
          return (
            <Button
              key={status}
              variant={statusFilter === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(status)}
              data-testid={`status-filter-${status}`}
              className={`h-7 text-xs gap-1 ${
                statusFilter === status 
                  ? '' 
                  : `${config.textColor} hover:${config.bgColor} border-${config.borderColor?.replace('border-', '')}`
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${config.dotColor}`} />
              {config.label}
            </Button>
          );
        })}
        {statusFilter !== 'all' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setStatusFilter('all')}
            className="h-7 text-xs text-stone-500 hover:text-stone-700"
          >
            Clear
          </Button>
        )}
      </div>

      {/* Search and Count */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="relative w-full sm:w-96">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-stone-400" />
          <Input
            placeholder="Search by name, order #, address..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="orders-search-input"
          />
        </div>
        <div className="flex items-center gap-2 text-sm text-stone-600">
          <Package className="h-4 w-4" />
          <span data-testid="orders-count">
            {orderCount} order{orderCount !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
      
      {/* Sort Buttons */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-stone-500">Group by:</span>
        <Button
          variant={sortBy === 'date' ? 'default' : 'outline'}
          size="sm"
          onClick={() => toggleSort('date')}
          data-testid="sort-by-date"
          className="gap-1"
        >
          Delivery Date
        </Button>
        <Button
          variant={sortBy === 'source' ? 'default' : 'outline'}
          size="sm"
          onClick={() => toggleSort('source')}
          data-testid="sort-by-source"
          className="gap-1"
        >
          Order Source
        </Button>
      </div>
    </div>
  );
};

export default OrderFilters;
